import { Component, effect, OnDestroy, OnInit, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { TablePageEvent } from 'primeng/table';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { globalSortFunction } from '../../../utilities/global-sort.util';
import { DeliveryNoteRoles } from '../../../models/roles/roles-group';
import { DeliveryNote, DeliveryNoteSearchCommand } from '../../../models/delivery-note/delivery-note';
import { WorkOrder } from '../../../models/order/work-order';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { DeliveryNoteProxyService } from '../../../proxies/order/delivery-note.proxy.service';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-delivery-notes',
  templateUrl: './delivery-notes.component.html',
  styleUrl: './delivery-notes.component.scss',
})
export class DeliveryNotesComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translate = inject(TranslateService);
  private deliveryNoteProxyService = inject(DeliveryNoteProxyService);
  private workOrderProxyService = inject(WorkOrderProxyService);

  isLoading = signal(0);
  listDeliveryNotes = signal<DeliveryNote[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'delivery-note-filters', config: this.filterConfig });
  showFilters = signal(false);
  workOrders = signal<WorkOrder[]>([]);
  filteredWorkOrders = signal<WorkOrder[]>([]);
  firstPagination = signal<number>(0);

  canEdit = computed(() => {
    if (AuthService.hasRole(DeliveryNoteRoles.DeliveryNoteAdmin)) {
      return true;
    }
    return false;
  });

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;

  getRowData(rowData: unknown): DeliveryNote {
    return rowData as DeliveryNote;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadWorkOrders();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('DeliveryNotes_Number', 'Delivery Note Number'),
          placeholder: translate('Enter_delivery_note_number', 'Enter delivery note number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'clientDocumentReference',
          label: translate('DeliveryNotes_Client_Document_Reference', 'Client Document Reference'),
          placeholder: translate('Enter_client_document_reference', 'Enter client document reference'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'workOrderReference',
          label: translate('DeliveryNotes_Work_Order', 'Work Order'),
          placeholder: translate('Select_work_order', 'Select work order'),
          autocompleteConfig: {
            suggestions: this.filteredWorkOrders(),
            optionLabel: 'orderNumber',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterWorkOrders(event),
            resolveById: (id: string) => this.resolveWorkOrder(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveWorkOrder(id: string): Observable<WorkOrder | null> {
    return this.workOrderProxyService.getWorkOrderById(id, false).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private loadWorkOrders(): void {
    this.workOrderProxyService.getWorkOrders().subscribe({
      next: (response) => {
        const workOrders = response.data ?? [];
        this.workOrders.set(workOrders);
        this.filteredWorkOrders.set(workOrders);
      },
      error: (e) => {
        console.error('Error loading work orders:', e);
        this.workOrders.set([]);
        this.filteredWorkOrders.set([]);
      },
    });
  }

  filterWorkOrders(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.workOrders().filter((wo) => wo.orderNumber?.toLowerCase().includes(query));
    this.filteredWorkOrders.set(filtered);
  }

  private searchDeliveryNotes(searchCriteria: DeliveryNoteSearchCommand): void {
    this.startLoading();
    this.deliveryNoteProxyService
      .searchDeliveryNotes(searchCriteria)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const deliveryNotes = response.data ?? [];
          this.listDeliveryNotes.set(deliveryNotes);
          this.onPageChange();
        },
        error: (e) => {
          console.error(e);
          this.listDeliveryNotes.set([]);
        },
      });
  }

  onPageChange(event?: TablePageEvent) {
    this.firstPagination.set(event?.first || 0);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: DeliveryNoteSearchCommand = {
      number: (filters['number'] as string)?.trim() || undefined,
      clientDocumentReference: (filters['clientDocumentReference'] as string)?.trim() || undefined,
      workOrderReference:
        (filters['workOrderReference'] as Record<string, string>)?.['id'] || (filters['workOrderReference'] as string) || undefined,
    };

    this.searchDeliveryNotes(searchCriteria);
  }

  public redirectToDetails(deliveryNoteId: string): void {
    const queryParams = { id: deliveryNoteId };
    this.router.navigate(['/delivery-note-detail'], { queryParams });
  }

  onTableSort(event: SortEvent): void {
    globalSortFunction<DeliveryNote>(event, ['number']);
  }

  getWorkOrderNumbers(deliveryNote: DeliveryNote): string {
    if (deliveryNote.workOrders && deliveryNote.workOrders.length > 0) {
      return deliveryNote.workOrders.map((wo) => wo.orderNumber).join(', ');
    }
    return deliveryNote.workOrderReferences?.join(', ') || '';
  }
}
